import React, { useState, useCallback, useMemo, useEffect } from "react";
import {
  Col,
  Row,
  Space,
  Table,
  Card,
  Button,
  message,
  Spin,
  Input,
  Tag,
  Statistic,
  Typography,
  Select,
  Radio,
  Steps,
  ConfigProvider,
  Dropdown,
  Checkbox,
  Segmented,
} from "antd";
import {
  ReloadOutlined,
  SearchOutlined,
  PieChartOutlined,
  FileSearchOutlined,
  CheckCircleOutlined,
  BarChartOutlined,
  FileTextOutlined,
  BankOutlined,
  FlagOutlined,
  FileDoneOutlined,
  PrinterOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  DownOutlined,
  TeamOutlined,
  SortAscendingOutlined,
  OrderedListOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import ReactECharts from "echarts-for-react";
import MotionHoc from "../../../utils/MotionHoc";
import {
  HEADERS_EXPORT_BEN,
  POST_LAWYER_JOB,
  POST_LOAN_DB2_V2,
} from "../../API/apiUrls";
import DetailContno from "./modal/DetailContno";
import {
  exportToExcel,
  exportToPDF,
  exportToExcelMeeting,
  exportToPDFMeeting,
  sortData,
} from "./report/ReportExport";
import {
  enrichCase,
  exportSummaryResultExcel,
} from "./report/SummaryResultExport";
import { CONTRACT_TYPES } from "../cancelHand/constant/CreateCancelHandOption";
import DateCustom from "../../../hook/DateCustom";

const { Text } = Typography;
const { Option } = Select;

// ─── น้ำหนักการเรียงสถานะ ─────────────────────────────────────────
const statusOrderMap = {
  ส่งฟ้อง: 1,
  เตรียมเอกสาร: 2,
  เตรียมเอกสารสำเร็จ: 3,
  เอกสารไม่ครบ: 3.1,
  สร้างคำฟ้อง: 4,
  ถอนฟ้อง: 4.1,
  อุทธรณ์: 4.2,
  รอเลขคดีดำ: 5,
  รอศาลพิพากษา: 6,
  พิพากษา: 7,
  พิพากษาทำยอม: 7.1,
  คำบังคับ: 7.2,
  คดีถึงที่สุด: 7.3,
  ตรวจคำพิพากษา: 8,
  ตรวจคำพิพากษาสำเร็จ: 8.1,
  บัญชีตีกลับ: 8.2,
  รอบัญชีตรวจ: 9,
  บัญชีตรวจสำเร็จ: 9.1,
  "รอ ceo อนุมัติ": 10,
  "ceo อนุมัติ": 10.1,
  "ปิดบัญชี (สัญญาใหม่)": 11,
  ปิดบัญชี: 11.1,
};

const MEETING_STATUSES = [
  "ส่งฟ้อง",
  "เตรียมเอกสาร",
  "เตรียมเอกสารสำเร็จ",
  "เอกสารไม่ครบ",
  "สร้างคำฟ้อง",
  // "ถอนฟ้อง",
  "อุทธรณ์",
  "รอเลขคดีดำ",
  "รอศาลพิพากษา",
  // "ปิดสัญญา",
];

const GROUPS = {
  prep: (s) =>
    s.includes("สืบทรัพย์") ||
    s.includes("บอกเลิก") ||
    s === "รอดำเนินการ" ||
    s === "งานธุรการ",

  prepLawsuit: (s) =>
    s.includes("เตรียมเอกสาร") ||
    s.includes("สร้างคำฟ้อง") ||
    s.includes("ส่งฟ้อง") ||
    s.includes("เอกสารไม่ครบ"),

  court: (s) =>
    s === "อุทธรณ์" ||
    s === "รอเลขคดีดำ" ||
    s === "รอศาลพิพากษา" ||
    s === "พิพากษา" ||
    s === "พิพากษาทำยอม" ||
    s === "คำบังคับ" ||
    s === "คดีถึงที่สุด",

  review: (s) =>
    s.includes("ceo") ||
    s.includes("บัญชี") ||
    s.includes("ตรวจ") ||
    s.includes("สัญญาใหม่") ||
    s.includes("ปิดบัญชี"),
};

// ถอนฟ้อง, ปิดบัญชีแต่ไม่มีผลคำพิพากษา → other
// currentStatus 26-27 (คืนงาน, จ่ายเป็นปัจจุบัน) → other, 15-25 → review
const getStatusGroup = (statusName, item = {}) => {
  const statusCode = Number(item.currentStatus);
  if (statusName === "ถอนฟ้อง") return "other";
  if (statusName === "ปิดบัญชี" && !item.idJudge) return "other";
  if (statusCode >= 26 && statusCode <= 27) return "other";
  if (statusCode >= 15 && statusCode <= 25) return "review";
  if (GROUPS.prep(statusName)) return "prep";
  if (GROUPS.prepLawsuit(statusName)) return "prepLawsuit";
  if (GROUPS.court(statusName)) return "court";
  if (GROUPS.review(statusName)) return "review";
  return "other";
};

// ─── Main Component ───────────────────────────────────────────────
const Main = () => {
  const USER_ID = parseInt(localStorage.getItem("USER_ID"));
  const ROLE_ID = parseInt(localStorage.getItem("ROLE_ID"));

  const [dataArr, setDataArr] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInputTable, setSearchInputTable] = useState("");
  const [selectedLawyerId, setSelectedLawyerId] = useState(null);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState([]);
  const [activeChartView, setActiveChartView] = useState("steps");
  const [isModal, setIsModal] = useState(false);
  const [dataRecord, setDataRecord] = useState(null);
  const [dataMeeting, setDataMeeting] = useState(false);
  const [dataSummary, setDataSummary] = useState(false);
  const [, convertDateThaiShort] = DateCustom();
  const [sortBy, setSortBy] = useState("contno");
  const [currentMacroStep, setCurrentMacroStep] = useState(5);

  // ─── 1. getTaskStatus ────────────────────────────────────────────
  const getTaskStatus = useCallback((item) => {
    const statusCode = Number(item.currentStatus);
    // สถานะ 18-25: ปิดบัญชีเฉพาะเมื่อ ACTIVE === 0 และ closeContno === 1, 26-27: ใช้ NAME_MAIN_STATUS
    if (statusCode >= 18 && statusCode <= 27) {
      if (
        statusCode <= 25 &&
        Number(item.ACTIVE) === 0 &&
        Number(item.closeContno) === 1
      ) {
        return "ปิดบัญชี";
      }
      return item.NAME_MAIN_STATUS || `สถานะรหัส ${item.currentStatus}`;
    }
    if (!item.ACTIVE) {
      return "ปิดบัญชี";
    }
    if (item.currentStatus === null || item.currentStatus === undefined) {
      if (item.roundInvestigate) return `เบลส่งสืบทรัพย์`;
      if (item.dateCeoInvestigate) return "เฮียส่งสืบทรัพย์";
      if (item.dateCancelContno) return "ส่งบอกเลิก";
      if (item.currentStatusAd) return "งานธุรการ";
      return "รอดำเนินการ";
    }
    const statusName =
      item.NAME_MAIN_STATUS || `สถานะรหัส ${item.currentStatus}`;
    if (item.newContno && item.closeContno === 0) {
      return "ปิดบัญชี (สัญญาใหม่)";
    }
    if (statusName.includes("ปิดบัญชี")) {
      return "ปิดบัญชี";
    }

    return statusName;
  }, []);

  // ─── 2. getStatusColor ───────────────────────────────────────────
  const getStatusColor = useCallback((status) => {
    console.log("status", status);

    if (!status || status.includes("เตรียมเอกสารสำเร็จ")) return "default";
    if (status.includes("สำเร็จ") || status.includes("อนุมัติ"))
      return "success";
    if (
      status.includes("ศาล") ||
      status.includes("พิพากษา") ||
      status.includes("บังคับ") ||
      status.includes("คดีดำ") ||
      status.includes("ถึงที่สุด")
    )
      return "processing";
    if (
      status.includes("บอกเลิก") ||
      status.includes("ไม่ครบ") ||
      status.includes("ตีกลับ") ||
      status.includes("ถอน")
    )
      return "error";
    if (status.includes("รอ") || status.includes("สืบทรัพย์")) return "warning";
    if (status.includes("เอกสาร") || status.includes("ฟ้อง")) return "geekblue";
    if (status.includes("สัญญาใหม่") || status.includes("ปิดบัญชี"))
      return "cyan";
    if (status.includes("ธุรการ")) return "magenta";
    return "default";
  }, []);

  // ─── 3. loadMainData ─────────────────────────────────────────────
  const loadMainData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        POST_LAWYER_JOB,
        { UserId: ROLE_ID === 3 ? USER_ID : 1 },
        { headers: HEADERS_EXPORT_BEN },
      );
      if (res.data?.data?.length > 0) {
        setDataArr(res.data.data);
      } else {
        setDataArr([]);
        message.info("ไม่พบข้อมูล");
      }
    } catch (error) {
      message.error(
        error.response?.data?.message ||
          error.message ||
          "ไม่สามารถโหลดข้อมูลได้",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMainData();
  }, [loadMainData]);

  // ─── 3.5 baseData: ใช้ meeting filter เป็นจุดตั้งต้น ─────────────
  const baseData = useMemo(() => {
    if (!dataMeeting) return dataArr;
    return dataArr.filter((d) => MEETING_STATUSES.includes(getTaskStatus(d)));
  }, [dataArr, dataMeeting, getTaskStatus]);

  // ─── 4. useMemo คำนวณ dashboard ──────────────────────────────────
  const { summary, chartData, lawyerStats, lawyerComparisonData } =
    useMemo(() => {
      const stats = { total: 0, prep: 0, prepLawsuit: 0, court: 0, review: 0 };
      const statusMap = {};
      const lawyerMap = {};
      const lawyerComparisonMap = {};

      baseData.forEach((item) => {
        const statusName = getTaskStatus(item);
        stats.total += 1;

        // ✅ ใช้ getStatusGroup เดียวกัน → Top Card และ Chart ตรงกัน 100%
        const group = getStatusGroup(statusName, item);
        if (group === "prep") stats.prep += 1;
        else if (group === "prepLawsuit") stats.prepLawsuit += 1;
        else if (group === "court") stats.court += 1;
        else if (group === "review") stats.review += 1;

        statusMap[statusName] = (statusMap[statusName] || 0) + 1;

        const uId = item.userId || "ยังไม่มอบหมาย";
        const username = item.NNAME || `${uId}`;

        if (!lawyerMap[uId]) {
          lawyerMap[uId] = { userId: uId, username, total: 0, statuses: {} };
        }
        lawyerMap[uId].total += 1;
        lawyerMap[uId].statuses[statusName] =
          (lawyerMap[uId].statuses[statusName] || 0) + 1;

        if (!lawyerComparisonMap[username]) {
          lawyerComparisonMap[username] = {
            username,
            prepCount: 0,
            courtCount: 0,
            targetTotal: 0,
          };
        }

        // ✅ ใช้ group เดียวกัน
        if (group === "prepLawsuit") {
          lawyerComparisonMap[username].prepCount += 1;
          lawyerComparisonMap[username].targetTotal += 1;
        }
        if (group === "court") {
          lawyerComparisonMap[username].courtCount += 1;
          lawyerComparisonMap[username].targetTotal += 1;
        }
      });

      const sortedChartData = Object.entries(statusMap)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => {
          const wA = statusOrderMap[a.name] ?? 99;
          const wB = statusOrderMap[b.name] ?? 99;
          return wA !== wB ? wA - wB : a.name.localeCompare(b.name);
        });

      const sortedComparisonData = Object.values(lawyerComparisonMap)
        .filter((d) => d.targetTotal > 0)
        .sort((a, b) => b.targetTotal - a.targetTotal);

      return {
        summary: stats,
        chartData: sortedChartData,
        lawyerStats: Object.values(lawyerMap).sort((a, b) => b.total - a.total),
        lawyerComparisonData: sortedComparisonData,
      };
    }, [baseData, getTaskStatus]);

  // ─── 5. statusOptions dropdown ───────────────────────────────────
  const statusOptions = useMemo(() => {
    const source = selectedLawyerId
      ? baseData.filter(
          (
            i, // ✅ baseData
          ) =>
            selectedLawyerId === "ยังไม่มอบหมาย"
              ? !i.userId
              : i.userId === selectedLawyerId,
        )
      : baseData; // ✅ baseData

    const unique = Array.from(new Set(source.map((i) => getTaskStatus(i))));
    return unique
      .sort((a, b) => {
        const wA = statusOrderMap[a] ?? 99;
        const wB = statusOrderMap[b] ?? 99;
        return wA !== wB ? wA - wB : a.localeCompare(b);
      })
      .map((s) => ({ value: s, label: s }));
  }, [baseData, getTaskStatus, selectedLawyerId]); // ✅ deps

  // ─── 6. detailTableData ──────────────────────────────────────────
  const detailTableData = useMemo(() => {
    let filtered = dataArr;
    if (selectedLawyerId) {
      filtered = filtered.filter((d) =>
        selectedLawyerId === "ยังไม่มอบหมาย"
          ? !d.userId
          : d.userId === selectedLawyerId,
      );
    }
    if (selectedStatusFilter && selectedStatusFilter.length > 0) {
      filtered = filtered.filter((d) => {
        const status = getTaskStatus(d);
        return selectedStatusFilter.some((s) => status.includes(s));
      });
    }

    if (dataMeeting) {
      filtered = filtered.filter((d) => {
        const status = getTaskStatus(d);
        return MEETING_STATUSES.includes(status);
      });
    }

    const query = searchInputTable.trim().toLowerCase();
    if (query) {
      filtered = filtered.filter((item) => {
        const fullName = `${item.SNAM || ""}${item.NAME1 || ""} ${item.NAME2 || ""}`;
        return (
          item.CONTNO?.toLowerCase().includes(query) ||
          item.newContno?.toLowerCase().includes(query) ||
          fullName.toLowerCase().includes(query)
        );
      });
    }

    return sortData(filtered, sortBy);
  }, [
    dataArr,
    dataMeeting,
    sortBy,
    selectedLawyerId,
    selectedStatusFilter,
    searchInputTable,
    getTaskStatus,
  ]);

  // ─── 7. ECharts options ──────────────────────────────────────────

  // 7.1 กราฟขั้นตอน (1 แท่งสีทอง)
  const barOptionsSteps = useMemo(
    () => ({
      tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "25%",
        top: "15%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: chartData.map((d) => d.name),
        axisLabel: { interval: 0, rotate: 45, color: "#595959", fontSize: 11 },
      },
      yAxis: {
        type: "value",
        minInterval: 1,
        splitLine: { lineStyle: { type: "dashed", color: "#f0f0f0" } },
      },
      series: [
        {
          name: "จำนวนคดี",
          type: "bar",
          barWidth: "40%",
          data: chartData.map((d) => d.count),
          itemStyle: { color: "#efa420", borderRadius: [4, 4, 0, 0] },
          label: {
            show: true,
            position: "top",
            color: "#595959",
            fontWeight: "bold",
            formatter: (p) => (p.value > 0 ? p.value : ""),
          },
        },
      ],
    }),
    [chartData],
  );

  // 7.2 กราฟทนาย (2 แท่ง ม่วง + แดง) ✅ fix barWidth ทั้งคู่
  const barOptionsLawyers = useMemo(
    () => ({
      tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
      legend: {
        data: ["เตรียมฟ้อง / เอกสาร", "ดำเนินชั้นศาล / พิพากษา"],
        top: 0,
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "10%",
        top: "15%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: lawyerComparisonData.map((d) => d.username),
        axisLabel: { interval: 0, rotate: 30, color: "#595959", fontSize: 11 },
      },
      yAxis: {
        type: "value",
        minInterval: 1,
        splitLine: { lineStyle: { type: "dashed", color: "#f0f0f0" } },
      },
      series: [
        {
          name: "เตรียมฟ้อง / เอกสาร",
          type: "bar",
          barWidth: "30%", // ✅ กำหนดทั้งคู่
          data: lawyerComparisonData.map((d) => d.prepCount),
          itemStyle: { color: "#722ed1", borderRadius: [4, 4, 0, 0] },
          label: {
            show: true,
            position: "top",
            formatter: (p) => (p.value > 0 ? p.value : ""),
            color: "#595959",
            fontWeight: "bold",
          },
        },
        {
          name: "ดำเนินชั้นศาล / พิพากษา",
          type: "bar",
          barWidth: "30%", // ✅ กำหนดทั้งคู่
          data: lawyerComparisonData.map((d) => d.courtCount),
          itemStyle: { color: "#f5222d", borderRadius: [4, 4, 0, 0] },
          label: {
            show: true,
            position: "top",
            formatter: (p) => (p.value > 0 ? p.value : ""),
            color: "#595959",
            fontWeight: "bold",
          },
        },
      ],
    }),
    [lawyerComparisonData],
  );

  // 7.3 กราฟวงกลม
  const pieOptions = useMemo(
    () => ({
      tooltip: {
        trigger: "item",
        formatter: "{a} <br/>{b}: {c} คดี ({d}%)",
      },
      legend: {
        type: "scroll",
        bottom: "0%",
        left: "center",
        itemStyle: { borderWidth: 0 },
      },
      series: [
        {
          name: "ปริมาณงานทนาย",
          type: "pie",
          radius: ["35%", "55%"],
          center: ["50%", "42%"],
          avoidLabelOverlap: true,
          itemStyle: { borderRadius: 8, borderColor: "#fff", borderWidth: 2 },
          label: {
            show: true,
            formatter: "{b}\n{c} คดี",
            fontWeight: "bold",
            color: "#595959",
            fontSize: 14,
          },
          labelLine: { show: true, length: 10, length2: 10 },
          data: lawyerStats.map((d) => ({ value: d.total, name: d.username })),
        },
      ],
    }),
    [lawyerStats],
  );

  // ─── 8. Columns ──────────────────────────────────────────────────
  const lawyerColumns = [
    {
      title: "ชื่อทนาย",
      dataIndex: "username",
      key: "username",
      render: (t) => <Text strong>{t}</Text>,
    },
    {
      title: "งานรวม",
      dataIndex: "total",
      key: "total",
      align: "center",
      render: (val) => (
        <Tag color="gold" style={{ fontWeight: "bold" }}>
          {val}
        </Tag>
      ),
    },
    (ROLE_ID === 1 || ROLE_ID === 2 || ROLE_ID === 5) && {
      title: "จัดการ",
      key: "action",
      align: "center",
      render: (_, record) => (
        <Button
          size="small"
          type={selectedLawyerId === record.userId ? "primary" : "default"}
          onClick={() =>
            setSelectedLawyerId(
              selectedLawyerId === record.userId ? null : record.userId,
              setSelectedStatusFilter([]),
              setSearchInputTable(""),
            )
          }
          style={
            selectedLawyerId === record.userId
              ? { backgroundColor: "#efa420", borderColor: "#efa420" }
              : {}
          }
        >
          {selectedLawyerId === record.userId ? "ซ่อน" : "ดูรายละเอียด"}
        </Button>
      ),
    },
  ].filter(Boolean);

  // ข้อมูลประชุม / รายงานสรุปผล เลือกได้ทีละอย่าง
  const onChangeCheckbox = (e) => {
    setDataMeeting(e.target.checked);
    if (e.target.checked) setDataSummary(false);
  };

  const onChangeSummaryCheckbox = (e) => {
    setDataSummary(e.target.checked);
    if (e.target.checked) setDataMeeting(false);
  };

  const detailColumns = [
    {
      title: "ลำดับ",
      key: "index",
      align: "center",
      width: 60,
      render: (_, __, i) => i + 1,
    },
    {
      title: "เลขสัญญา",
      dataIndex: "CONTNO",
      key: "CONTNO",
      align: "center",
      render: (_, record) => (
        <Tag
          color="blue"
          style={{ cursor: "pointer" }}
          onClick={() => {
            console.log(record);
            setDataRecord(record);
            setIsModal(true);
          }}
        >
          {record.CONTNO}
        </Tag>
      ),
    },
    {
      title: "ชื่อ-นามสกุลจำเลย",
      key: "fullName",
      render: (_, r) => `${r.SNAM || ""}${r.NAME1 || ""} ${r.NAME2 || ""}`,
    },
    {
      title: "ทนาย",
      dataIndex: "NNAME",
      key: "NNAME",
      align: "center",
    },
    {
      title: "สถานะงานปัจจุบัน",
      key: "status",
      align: "center",
      render: (_, record) => {
        const subStatus =
          record.statusActiveInvestigate === 3
            ? "เจอทรัพย์"
            : record.statusActiveInvestigate === 4
              ? "ไม่เจอทรัพย์"
              : record.statusActiveInvestigate === 2
                ? "ส่งสืบแล้ว"
                : record.statusActiveInvestigate === 1
                  ? "รอส่งสืบทรัพย์"
                  : record.statusActiveInvestigate === 0
                    ? "ยังไม่ส่งสืบ"
                    : null;
        const s = getTaskStatus(record);

        return (
          <Tag
            color={
              s === "เบลส่งสืบทรัพย์"
                ? record.statusActiveInvestigate === 3
                  ? "green"
                  : record.statusActiveInvestigate === 4
                    ? "red"
                    : record.statusActiveInvestigate === 2
                      ? "blue"
                      : record.statusActiveInvestigate === 1
                        ? "orange"
                        : record.statusActiveInvestigate === 0
                          ? "gray"
                          : "gray"
                : getStatusColor(s)
            }
          >
            {s}
            {s === "เบลส่งสืบทรัพย์" ? `(${subStatus})` : null}
          </Tag>
        );
      },
    },
  ];

  const queryMultiData = useCallback(async (payload) => {
    if (!payload || Object.keys(payload).length === 0) {
      message.error("ไม่มีข้อมูลสำหรับการค้นหา");
      return [];
    }

    setLoading(true);
    try {
      const allContracts = [];
      CONTRACT_TYPES.forEach((type) => {
        if (payload[type]) {
          payload[type]
            .split(",")
            .map((c) => c.trim().replace(/'/g, ""))
            .filter(Boolean)
            .forEach((contno) => allContracts.push({ type, contno }));
        }
      });

      if (allContracts.length === 0) {
        message.warning("ไม่มีเลขสัญญาที่ต้องค้นหา");
        return [];
      }

      const chunkSize = 25;
      const chunks = [];
      for (let i = 0; i < allContracts.length; i += chunkSize) {
        chunks.push(allContracts.slice(i, i + chunkSize));
      }

      const allResults = [];
      for (let i = 0; i < chunks.length; i++) {
        const chunkPayload = CONTRACT_TYPES.reduce((acc, type) => {
          const contractsOfType = chunks[i]
            .filter((item) => item.type === type)
            .map((item) => `'${item.contno}'`);
          acc[type] = contractsOfType.length ? contractsOfType.join(",") : "";
          return acc;
        }, {});

        try {
          const response = await axios.post(POST_LOAN_DB2_V2, chunkPayload, {
            headers: HEADERS_EXPORT_BEN,
          });
          if (response.status === 200 && response.data) {
            const chunkData = Array.isArray(response.data)
              ? response.data
              : [response.data];
            allResults.push(...chunkData);
          }
        } catch (chunkError) {
          console.error(`Chunk ${i + 1} failed:`, chunkError);
        }

        if (i < chunks.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }

      if (allResults.length === 0) {
        message.warning("ไม่พบข้อมูลสัญญาที่ค้นหา");
      } else if (allResults.length < allContracts.length) {
        message.warning(
          `พบข้อมูล ${allResults.length} จาก ${allContracts.length} สัญญา`,
        );
      }

      return allResults; // ✅ ส่งข้อมูลกลับ
    } catch (error) {
      console.error("Error in queryMultiData:", error);
      const status = error?.response?.status;
      message.error(
        status === 404
          ? "ไม่พบเลขที่สัญญา"
          : `เกิดข้อผิดพลาดในการดึงข้อมูล${status ? ` (${status})` : ""}`,
      );
      return []; // ✅ คืน array เปล่าเมื่อ error ไม่ใช่ undefined
    } finally {
      setLoading(false);
    }
  }, []);
  const handleDataQuery = useCallback(
    (rows = []) => {
      const buckets = CONTRACT_TYPES.reduce((acc, type) => {
        acc[type] = [];
        return acc;
      }, {});

      rows.forEach((row) => {
        const type = String(row?.SCHEMA || "").toLowerCase();
        const contno = row?.CONTNO?.trim();
        if (
          contno &&
          CONTRACT_TYPES.includes(type) &&
          !buckets[type].includes(contno)
        ) {
          buckets[type].push(contno);
        }
      });

      const payload = CONTRACT_TYPES.reduce((acc, type) => {
        acc[type] = buckets[type].length
          ? buckets[type].map((c) => `'${c}'`).join(",")
          : "";
        return acc;
      }, {});

      return queryMultiData(payload); // ✅ return promise
    },
    [queryMultiData],
  );

  const handleDataExport = useCallback(() => {
    if (detailTableData.length > 0) {
      return handleDataQuery(detailTableData); // ✅ return promise
    }
    return Promise.resolve([]); // ✅ ไม่มีข้อมูล → คืน array เปล่า
  }, [detailTableData, handleDataQuery]);

  const mergeDB2IntoDetail = (detailRows = [], db2Rows = []) => {
    const norm = (v) =>
      String(v ?? "")
        .trim()
        .replace(/'/g, "");

    // CONTNO ของ db2 อยู่ใน LOAN.CONTNO (เผื่อ fallback ระดับบน)
    const db2Map = new Map();
    (Array.isArray(db2Rows) ? db2Rows : []).forEach((item) => {
      const contno = norm(item?.LOAN?.CONTNO ?? item?.CONTNO);
      if (contno) db2Map.set(contno, item);
    });

    // แปะ db2 เข้า detail ตาม CONTNO ที่ตรงกัน
    return detailRows.map((row) => {
      const db2 = db2Map.get(norm(row?.CONTNO)) || null;
      return { ...row, db2 }; // เก็บก้อน db2 ไว้เป็น field เดียว
    });
  };

  console.log("detailTableData", detailTableData);

  // ✅ Dropdown menu เลือก PDF หรือ Excel
  const exportMenuItems = {
    items: [
      {
        key: "pdf",
        label: "Export PDF",
        icon: <FilePdfOutlined style={{ color: "#c0392b" }} />,
        // รายงานสรุปผล export ได้แค่ excel
        disabled: dataSummary,
      },
      {
        key: "excel",
        label: "Export Excel",
        icon: <FileExcelOutlined style={{ color: "#27ae60" }} />,
      },
    ],
    onClick: async ({ key }) => {
      const selectedLawyerName = selectedLawyerId
        ? lawyerStats.find((l) => l.userId === selectedLawyerId)?.username ||
          selectedLawyerId
        : "";

      if (dataSummary) {
        if (key !== "excel") return;
        // ไม่เลือกทนาย -> เอาเฉพาะสัญญาที่กำหนดทนายแล้ว (ตัด ยังไม่มอบหมาย)
        const exportRows = selectedLawyerId
          ? detailTableData
          : detailTableData.filter((d) => d.userId);
        if (exportRows.length === 0) {
          message.warning("ไม่มีข้อมูลสำหรับ export");
          return;
        }
        try {
          const dataDB2 = await handleDataQuery(exportRows);
          setLoading(true);
          const rows = mergeDB2IntoDetail(exportRows, dataDB2).map((item) =>
            enrichCase(item, getTaskStatus),
          );
          const name = selectedLawyerName || "ทนายทั้งหมด";
          await exportSummaryResultExcel({
            rows,
            subtitle: `${name} | ข้อมูล ณ วันที่ ${dayjs().format(
              "DD/MM/YYYY HH:mm",
            )}`,
            formatDate: convertDateThaiShort,
            groupOf: getStatusGroup,
            showLawyer: !selectedLawyerId,
            fileName: `รายงานสรุปผล_${name}_${dayjs().format(
              "YYYYMMDD_HHmm",
            )}.xlsx`,
          });
        } catch (error) {
          console.error(error);
          message.error("Export Excel ไม่สำเร็จ");
        } finally {
          setLoading(false);
        }
        return;
      }

      if (key === "pdf" && !dataMeeting) {
        exportToPDF(detailTableData, getTaskStatus, selectedLawyerName);
        return;
      }
      if (key === "pdf" && dataMeeting) {
        exportToPDFMeeting(detailTableData, getTaskStatus, selectedLawyerName);
        return;
      }
      if (key === "excel" && !dataMeeting) {
        let dataDB2 = {};
        if (selectedLawyerId && selectedLawyerId !== "ยังไม่มอบหมาย") {
          dataDB2 = await handleDataExport();
        } else {
          message.warning(
            "ถ้าไม่เลือกทนาย จะไม่สามารถดูข้อมูลลูกหนี้คงเหลือได้ !!",
          );
        }

        const merged = mergeDB2IntoDetail(detailTableData, dataDB2);
        console.log("merged", merged);
        exportToExcel(merged, getTaskStatus);
        return;
      }
      if (key === "excel" && dataMeeting) {
        exportToExcelMeeting(detailTableData, getTaskStatus);
      }
    },
  };

  // ─── 9. Render ───────────────────────────────────────────────────
  return (
    <div
      style={{
        backgroundColor: "#f0f2f5",
        minHeight: "100vh",
        paddingBottom: 24,
      }}
    >
      {/* Header */}
      <Card
        style={{
          marginBottom: 16,
          borderRadius: 12,
          border: "1px solid #ffcb05",
          background: "#fffdf0",
          padding: "16px 24px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 30,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 16px",
                borderRadius: 8,
                background: "#fff3cc",
                border: "1px solid #ffcb05",
              }}
            >
              <BarChartOutlined style={{ fontSize: 18, color: "#efa420" }} />
              <span
                style={{ fontSize: 16, fontWeight: "bold", color: "#efa420" }}
              >
                ภาพรวมงานทนายความ
              </span>
            </div>
          </div>
          <Button
            icon={<ReloadOutlined />}
            onClick={loadMainData}
            style={{
              borderRadius: 8,
              borderColor: "#efa420",
              color: "#efa420",
            }}
          >
            โหลดข้อมูลใหม่
          </Button>
        </div>
        <ConfigProvider
          theme={{
            token: {
              colorPrimary: "#efa420",
            },
          }}
        >
          <Steps
            current={currentMacroStep}
            size="small"
            items={[
              {
                title: "เตรียมข้อมูล",
                description: "ออกบอกเลิก, ส่งสืบทรัพย์ ,แจ้งส่งฟ้อง",
                icon: <FileTextOutlined />,
              },
              {
                title: "เตรียมฟ้อง",
                description: "เตรียมเอกสาร, ร่างคำฟ้อง, รอวันนัดศาล",
                icon: <FileDoneOutlined />,
              },
              {
                title: "ดำเนินชั้นศาล",
                description: "พิพากษา, คำบังคับ, คดีถึงที่สุด",
                icon: <BankOutlined />,
              },
              {
                title: "ตรวจสอบ",
                description: "เบลตรวจคำพิพากษา, บัญชีตรวจ",
                icon: <FileSearchOutlined />,
              },
              {
                title: "อนุมัติ",
                description: "รอ CEO อนุมัติ",
                icon: <CheckCircleOutlined />,
              },
              {
                title: "สิ้นสุด",
                description: "ปิดบัญชี, สัญญาใหม่",
                icon: <FlagOutlined />,
              },
            ]}
          />
        </ConfigProvider>
      </Card>

      <Spin spinning={loading} size="large" tip="กำลังโหลดและประมวลผลข้อมูล...">
        {/* Top Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          {[
            {
              title: "สัญญาทั้งหมด",
              value: summary.total,
              color: "#1890ff",
              icon: <PieChartOutlined style={{ color: "#1890ff" }} />,
              span: { xs: 24, sm: 12, md: 8, lg: 5 },
            },
            {
              title: "สืบทรัพย์ / ส่งบอกเลิก",
              value: summary.prep,
              color: "#faad14",
              icon: <FileSearchOutlined style={{ color: "#faad14" }} />,
              span: { xs: 24, sm: 12, md: 8, lg: 4 },
            },
            {
              title: "เตรียมฟ้อง / เอกสาร",
              value: summary.prepLawsuit,
              color: "#722ed1",
              icon: <FileTextOutlined style={{ color: "#722ed1" }} />,
              span: { xs: 24, sm: 12, md: 8, lg: 5 },
            },
            {
              title: "ดำเนินชั้นศาล / พิพากษา",
              value: summary.court,
              color: "#f5222d",
              icon: <BankOutlined style={{ color: "#f5222d" }} />,
              span: { xs: 24, sm: 12, md: 8, lg: 5 },
            },
            {
              title: "ตรวจสอบ / สัญญาใหม่",
              value: summary.review,
              color: "#52c41a",
              icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
              span: { xs: 24, sm: 12, md: 8, lg: 5 },
            },
          ].map((card, i) => (
            <Col key={i} {...card.span} style={{ display: "flex" }}>
              <Card
                variant={false}
                style={{
                  width: "100%",
                  borderRadius: 12,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                }}
              >
                <Statistic
                  title={card.title}
                  value={card.value}
                  prefix={card.icon}
                  valueStyle={{
                    color: card.color,
                    fontWeight: 600,
                    fontSize: 22,
                  }}
                />
              </Card>
            </Col>
          ))}
        </Row>

        {/* Charts */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} lg={16}>
            <Card
              variant={false}
              style={{ borderRadius: 12, height: "100%" }}
              title={
                <Space
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <span style={{ color: "#595959" }}>📊 สถิติการดำเนินการ</span>
                  <Radio.Group
                    value={activeChartView}
                    onChange={(e) => setActiveChartView(e.target.value)}
                    optionType="button"
                    buttonStyle="solid"
                    size="small"
                  >
                    <Radio.Button value="steps">
                      ปริมาณงานแยกตามขั้นตอน
                    </Radio.Button>
                    <Radio.Button value="lawyers">
                      เปรียบเทียบภาระงานทนาย
                    </Radio.Button>
                  </Radio.Group>
                </Space>
              }
            >
              <ReactECharts
                key={activeChartView}
                option={
                  activeChartView === "steps"
                    ? barOptionsSteps
                    : barOptionsLawyers
                }
                notMerge={true}
                lazyUpdate={false}
                style={{ height: 380, width: "100%" }}
              />
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card
              title={
                <span style={{ color: "#595959" }}>
                  👨‍⚖️ สัดส่วนคดีแยกตามทนาย
                </span>
              }
              variant={false}
              style={{ borderRadius: 12, height: "100%" }}
            >
              <ReactECharts
                option={pieOptions}
                style={{ height: 380, width: "100%" }}
              />
            </Card>
          </Col>
        </Row>

        {/* Tables */}
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={8}>
            <Card
              title={
                <Space>
                  <TeamOutlined style={{ color: "#1677ff" }} />
                  สรุปงานรายบุคคล
                </Space>
              }
              extra={
                <Dropdown menu={exportMenuItems} trigger={["click"]}>
                  <Button type="primary" ghost icon={<PrinterOutlined />}>
                    พิมพ์ / Export <DownOutlined />
                  </Button>
                </Dropdown>
              }
              variant={false}
              style={{ borderRadius: 12, height: "100%" }}
            >
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  padding: "10px 14px",
                  marginBottom: 12,
                  background: "#fafafa",
                  border: "1px solid #f0f0f0",
                  borderRadius: 10,
                }}
              >
                <Space size={8}>
                  <Checkbox onChange={onChangeCheckbox} checked={dataMeeting}>
                    เลือกข้อมูลประชุม
                  </Checkbox>
                  <Checkbox
                    onChange={onChangeSummaryCheckbox}
                    checked={dataSummary}
                  >
                    เลือกรายงานสรุปผล
                  </Checkbox>
                </Space>
                <Space size={8}>
                  <Text type="secondary">เรียงตาม</Text>
                  <Segmented
                    value={sortBy}
                    onChange={setSortBy}
                    options={[
                      {
                        label: "เลขสัญญา",
                        value: "contno",
                        icon: <SortAscendingOutlined />,
                      },
                      {
                        label: "สถานะงาน",
                        value: "status",
                        icon: <OrderedListOutlined />,
                      },
                    ]}
                  />
                </Space>
              </div>
              <Table
                columns={lawyerColumns}
                dataSource={lawyerStats}
                rowKey="userId"
                pagination={false}
                size="small"
                rowClassName={(record) =>
                  record.userId === selectedLawyerId
                    ? "ant-table-row-selected"
                    : ""
                }
              />
            </Card>
          </Col>

          <Col xs={24} lg={16}>
            <Card
              title={
                selectedLawyerId
                  ? `รายการคดีของ: ${
                      lawyerStats.find((l) => l.userId === selectedLawyerId)
                        ?.username || "ยังไม่มอบหมาย"
                    }`
                  : "รายการคดีทั้งหมดในระบบ"
              }
              variant={false}
              style={{ borderRadius: 12, height: "100%" }}
              extra={
                <Space wrap>
                  <Select
                    size="large"
                    style={{ minWidth: 200 }}
                    placeholder="📌 กรองตามสถานะ"
                    value={selectedStatusFilter}
                    allowClear
                    onChange={(v) => {
                      setSelectedStatusFilter(v);
                      setSearchInputTable("");
                    }}
                    mode="multiple"
                  >
                    {statusOptions.map(({ value, label }) => (
                      <Option key={value} value={value}>
                        {label}
                      </Option>
                    ))}
                  </Select>

                  <Input
                    placeholder="ค้นหาเลขสัญญา, ชื่อ..."
                    value={searchInputTable}
                    onChange={(e) => setSearchInputTable(e.target.value)}
                    style={{ width: 180 }}
                    prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                    allowClear
                    size="small"
                  />

                  {selectedLawyerId && (
                    <Button
                      type="dashed"
                      size="small"
                      onClick={() => setSelectedLawyerId(null)}
                    >
                      ดูทุกคน
                    </Button>
                  )}
                </Space>
              }
            >
              <Table
                size="small"
                columns={detailColumns}
                dataSource={detailTableData}
                rowKey="loanId"
                scroll={{ x: 600 }}
                pagination={{
                  defaultPageSize: 15,
                  showSizeChanger: true,
                  showTotal: (total) => `ทั้งหมด ${total} รายการ`,
                  pageSizeOptions: ["10", "20", "50"],
                }}
              />
            </Card>
          </Col>
        </Row>
        {isModal && (
          <DetailContno
            open={isModal}
            close={() => setIsModal(false)}
            dataDefault={dataRecord}
          />
        )}
      </Spin>
    </div>
  );
};

const DashboradLawyerJob = MotionHoc(Main);
export default DashboradLawyerJob;
